/**
 * RegistryBackfill — the one-time CRM → eReg migration checklist
 * (source-of-truth plan §7.3–§7.4). Admin-only.
 *
 * Per study: link state (with suggested registry matches for unlinked rows —
 * one-click link), legacy-document migration state (copy bytes + extracted
 * text into eReg, idempotent), and key-document slot state (pinning is a
 * HUMAN act in eReg — newest ≠ current). Once every row reads linked +
 * migrated + pinned, the owner freezes crm.study_documents
 * (CRM_STUDY_DOCS_FROZEN=true on the api service); legacy rows/bytes stay
 * readable ≥7 days for already-texted ICF links.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Check, Copy, DatabaseZap, Link2, Loader2, RefreshCw, Search, Snowflake } from 'lucide-react';
import api from '../../../services/api';
import { PageHeader } from '../../components';
import LinkRegistryModal from '../../components/studies/LinkRegistryModal';
import { eregStudyLink } from '../../../ui/returnTo';

interface Suggestion {
  protocol_id: string;
  protocol_number: string | null;
  study_nickname: string | null;
  sponsor_name: string | null;
  site_name: string | null;
  score: number;
}

interface BackfillRow {
  id: string;
  slug: string;
  title: string | null;
  internal_title: string | null;
  status: string | null;
  protocol_number: string | null;
  protocol_id: string | null;
  legacy_docs: number;
  migrated_docs: number;
  slots: { protocol: boolean; icf: boolean } | null;
  suggestions: Suggestion[];
}

interface BackfillStatus {
  studies: BackfillRow[];
  registry_reads_enabled: boolean;
  study_docs_frozen: boolean;
}

const chip = (tone: 'ok' | 'warn' | 'muted', label: string) => {
  const tones = {
    ok: 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400',
    warn: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
    muted: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
  };
  return (
    <span className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ${tones[tone]}`}>
      {tone === 'ok' && <Check className="h-3 w-3" />}
      {label}
    </span>
  );
};

export default function RegistryBackfill() {
  const [status, setStatus] = useState<BackfillStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [linkFor, setLinkFor] = useState<BackfillRow | null>(null);
  const [busy, setBusy] = useState<string | null>(null); // study id being linked/copied
  const [notes, setNotes] = useState<Record<string, string>>({}); // per-study result note

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/crm/registry-backfill');
      setStatus(res.data);
      setError(null);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to load backfill status');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const linkSuggestion = async (row: BackfillRow, s: Suggestion) => {
    setBusy(row.id);
    setNotes((n) => ({ ...n, [row.id]: '' }));
    try {
      await api.post(`/api/crm/studies/${row.id}/link-registry`, { protocol_id: s.protocol_id });
      await load();
    } catch (err: any) {
      setNotes((n) => ({ ...n, [row.id]: err?.response?.data?.error || 'Link failed' }));
    } finally {
      setBusy(null);
    }
  };

  const copyDocs = async (row: BackfillRow) => {
    setBusy(row.id);
    setNotes((n) => ({ ...n, [row.id]: '' }));
    try {
      const res = await api.post(`/api/crm/studies/${row.id}/registry-backfill-docs`, {});
      const { migrated = 0, skipped = 0, failed = 0, documents = [] } = res.data || {};
      const failures = (documents as { status: string; filename: string; error?: string }[])
        .filter((d) => d.status === 'failed')
        .slice(0, 3)
        .map((d) => `${d.filename}${d.error ? ` — ${d.error}` : ''}`);
      setNotes((n) => ({
        ...n,
        [row.id]: `Copied ${migrated}, already migrated ${skipped}${failed ? `, failed ${failed}: ${failures.join('; ')}` : ''}.`,
      }));
      await load();
    } catch (err: any) {
      setNotes((n) => ({ ...n, [row.id]: err?.response?.data?.error || 'Copy failed' }));
    } finally {
      setBusy(null);
    }
  };

  const rows = status?.studies || [];
  const allDone = rows.length > 0 && rows.every(
    (r) => r.protocol_id && r.migrated_docs >= r.legacy_docs && (r.legacy_docs === 0 || (r.slots?.protocol || r.slots?.icf))
  );

  return (
    <div className="space-y-4">
      <PageHeader
        icon={DatabaseZap}
        title="Registry backfill"
        subtitle="One-time migration: link every CRM study to eReg, copy its legacy documents, then pin the current protocol/ICF in eReg."
        actions={
          <button
            type="button"
            onClick={load}
            aria-label="Refresh"
            className="inline-flex h-11 w-11 items-center justify-center rounded-lg border border-gray-300 text-gray-600 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700 sm:h-8 sm:w-8"
          >
            <RefreshCw className="h-4 w-4" />
          </button>
        }
      />

      {status?.study_docs_frozen ? (
        <div className="flex items-center gap-2.5 rounded-xl border border-primary-200 bg-primary-50 p-3 text-sm text-primary-900 dark:border-primary-800 dark:bg-primary-900/20 dark:text-primary-300">
          <Snowflake className="h-4 w-4 flex-shrink-0" />
          Legacy study-document uploads are frozen — eReg is the only write path. Legacy files stay readable for texted ICF links.
        </div>
      ) : allDone ? (
        <div className="rounded-xl border border-green-200 bg-green-50 p-3 text-sm text-green-800 dark:border-green-800 dark:bg-green-900/20 dark:text-green-300">
          Every study is linked, migrated, and pinned. Final step (owner): set <code className="font-mono text-xs">CRM_STUDY_DOCS_FROZEN=true</code> on
          the api service to freeze legacy uploads (keep legacy files readable ≥7 days for already-texted ICF links).
        </div>
      ) : null}

      {loading ? (
        <div className="flex h-40 items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-primary-600 dark:text-primary-400" />
        </div>
      ) : error ? (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-900/20 dark:text-red-300">{error}</div>
      ) : rows.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-300 p-10 text-center text-sm text-gray-500 dark:border-gray-600 dark:text-gray-400">
          No studies to migrate.
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
          <ul className="divide-y divide-gray-100 dark:divide-gray-700">
            {rows.map((row) => {
              const linked = !!row.protocol_id;
              const migrated = row.legacy_docs === 0 || row.migrated_docs >= row.legacy_docs;
              const pinned = !!(row.slots?.protocol || row.slots?.icf);
              return (
                <li key={row.id} className="p-4">
                  <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-2">
                    <div className="min-w-0 flex-1">
                      <Link
                        to={`/crm/settings/studies/${row.id}`}
                        className="block truncate text-sm font-semibold text-gray-900 hover:text-primary-700 dark:text-gray-100 dark:hover:text-primary-400"
                      >
                        {row.internal_title || row.title || row.slug}
                      </Link>
                      <p className="font-mono text-xs text-gray-400 dark:text-gray-500">
                        {row.slug}{row.protocol_number ? ` · #${row.protocol_number}` : ''}
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-1.5">
                      {linked ? chip('ok', 'Linked') : chip('warn', 'Not linked')}
                      {row.legacy_docs > 0
                        ? (migrated ? chip('ok', `Docs ${row.migrated_docs}/${row.legacy_docs}`) : chip('warn', `Docs ${row.migrated_docs}/${row.legacy_docs}`))
                        : chip('muted', 'No legacy docs')}
                      {linked && (
                        pinned
                          ? chip('ok', `Pinned${row.slots?.protocol ? ' · protocol' : ''}${row.slots?.icf ? ' · ICF' : ''}`)
                          // Pinning is only a BACKFILL requirement when legacy
                          // docs were copied (mirrors the allDone rule) —
                          // doc-less studies pin as they upload in eReg.
                          : row.legacy_docs > 0 ? chip('warn', 'Slots not pinned') : chip('muted', 'No slots pinned yet')
                      )}
                    </div>
                  </div>

                  {!linked && row.suggestions.length > 0 && (
                    <div className="mt-2 space-y-1.5">
                      {row.suggestions.map((s) => (
                        <div key={s.protocol_id} className="flex flex-wrap items-center gap-2 rounded-lg border border-gray-200 px-2.5 py-1.5 dark:border-gray-700">
                          <span className="min-w-0 flex-1 truncate text-xs text-gray-700 dark:text-gray-300">
                            <span className="font-medium">{s.protocol_number || '—'}</span>
                            {[s.study_nickname, s.sponsor_name, s.site_name].filter(Boolean).map((v) => ` · ${v}`).join('')}
                            <span className="ml-1 text-gray-400">({s.score}% match)</span>
                          </span>
                          <button
                            type="button"
                            onClick={() => linkSuggestion(row, s)}
                            disabled={busy === row.id}
                            className="inline-flex min-h-[44px] items-center gap-1 rounded-lg border border-primary-200 bg-primary-50 px-2.5 py-1 text-xs font-medium text-primary-700 transition-colors duration-150 hover:bg-primary-100 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 dark:border-primary-800 dark:bg-primary-900/30 dark:text-primary-300 sm:min-h-0"
                          >
                            {busy === row.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Link2 className="h-3.5 w-3.5" />}
                            Link
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    {!linked && (
                      <button
                        type="button"
                        onClick={() => setLinkFor(row)}
                        disabled={busy === row.id}
                        className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border border-gray-300 px-2.5 py-1.5 text-xs font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700 sm:min-h-0"
                      >
                        <Search className="h-3.5 w-3.5" /> Search eReg…
                      </button>
                    )}
                    {linked && row.legacy_docs > 0 && !migrated && (
                      <button
                        type="button"
                        onClick={() => copyDocs(row)}
                        disabled={busy === row.id}
                        className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg bg-primary-600 px-2.5 py-1.5 text-xs font-medium text-white transition-colors duration-150 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 sm:min-h-0"
                      >
                        {busy === row.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Copy className="h-3.5 w-3.5" />}
                        Copy documents to eReg
                      </button>
                    )}
                    {linked && (
                      <Link
                        to={eregStudyLink(row.protocol_id!, { returnTo: '/crm/settings/registry-backfill', returnLabel: 'Registry backfill' })}
                        className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border border-gray-300 px-2.5 py-1.5 text-xs font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700 sm:min-h-0"
                      >
                        <ArrowUpRight className="h-3.5 w-3.5 text-primary-600 dark:text-primary-400" />
                        {pinned ? 'Open in eReg' : 'Pin key documents in eReg'}
                      </Link>
                    )}
                  </div>
                  {notes[row.id] && (
                    <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">{notes[row.id]}</p>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {linkFor && (
        <LinkRegistryModal
          studyId={linkFor.id}
          studyLabel={linkFor.internal_title || linkFor.title || linkFor.slug}
          onClose={() => setLinkFor(null)}
          onLinked={() => { setLinkFor(null); load(); }}
        />
      )}
    </div>
  );
}
